import { useState, useEffect } from "react";
import axios from "axios";
import { CreditCard, TrendingUp, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchRevenue(); }, [days]);

  const fetchRevenue = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/revenue?days=${days}`);
      setData(res.data);
    } catch { /* handled by auth */ }
    finally { setLoading(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  if (loading || !data) {
    return <div style={{ textAlign: "center", padding: "4rem" }}><div className="processing-spinner" /></div>;
  }

  return (
    <div className="page-container fade-in" data-testid="admin-revenue-page">
      <div className="page-header">
        <div><h1>Revenue</h1><p className="page-subtitle">Financial overview and payment history.</p></div>
        <div className="jt-filters" style={{ flexWrap: "nowrap" }}>
          {[7, 30, 90].map(d => (
            <button key={d} className={`jt-filter-btn ${days === d ? "active" : ""}`}
              onClick={() => setDays(d)} data-testid={`rev-filter-${d}d`}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Revenue Summary */}
      <div className="admin-stats-grid" data-testid="admin-revenue-summary">
        <div className="admin-stat-card accent">
          <CreditCard size={18} />
          <div>
            <div className="admin-stat-value" data-testid="rev-total">{data.total_display}</div>
            <div className="admin-stat-label">Total Revenue ({data.period_days}d)</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <TrendingUp size={18} />
          <div>
            <div className="admin-stat-value">{data.subscription_revenue}</div>
            <div className="admin-stat-label">Subscription Revenue</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <Package size={18} />
          <div>
            <div className="admin-stat-value">{data.addon_revenue}</div>
            <div className="admin-stat-label">Add-on Revenue</div>
          </div>
        </div>
      </div>

      {/* Payment List */}
      <h2 className="admin-section-title" style={{ marginTop: "1.5rem" }}>
        <CreditCard size={16} /> Recent Payments ({data.payment_count})
      </h2>
      {data.payments.length === 0 ? (
        <div className="dash-empty-card"><h3>No payments in this period</h3><p>Try expanding the time range.</p></div>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-payments-table">
          <table className="admin-table">
            <thead>
              <tr><th>Type</th><th>Plan/Pack</th><th>Amount</th><th>Payment ID</th><th>Date</th></tr>
            </thead>
            <tbody>
              {data.payments.map((p, i) => (
                <tr key={i} data-testid={`payment-row-${i}`}>
                  <td><span className={`admin-type-pill ${p.type}`}>{p.type}</span></td>
                  <td>{p.plan_id || p.pack_id || "—"}</td>
                  <td className="admin-amount">{p.currency === "INR" ? "₹" : "$"}{(p.amount / 100).toLocaleString()}</td>
                  <td className="admin-payment-id">{p.razorpay_payment_id || "—"}</td>
                  <td>{formatDate(p.paid_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
